import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../hooks/useAuth';
import { campaignService } from '../../services/campaigns';
import { configService } from '../../services/config';
import faqService from '../../services/faq';
import api from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';
import type { FaqQuestion } from '../../types/faq';
import phpTemplate from '../../templates/axetra-integration.template.php?raw';

type IntegrationType = 'php' | 'javascript' | 'python' | 'wordpress';

interface Integration {
  id: IntegrationType;
  name: string;
  description: string;
  icon: string;
  fileExtension: string;
  available: boolean;
}

const integrations: Integration[] = [
  {
    id: 'php',
    name: 'PHP Script',
    description: 'Drop-in PHP script for any PHP-based website or application',
    icon: '🐘',
    fileExtension: 'php',
    available: true,
  },
  {
    id: 'javascript',
    name: 'JavaScript SDK',
    description: 'Client-side JavaScript integration for web applications',
    icon: '⚡',
    fileExtension: 'js',
    available: false,
  },
  {
    id: 'python',
    name: 'Python Script',
    description: 'Python integration for Flask, Django, or FastAPI applications',
    icon: '🐍',
    fileExtension: 'py',
    available: false,
  },
  {
    id: 'wordpress',
    name: 'WordPress Plugin',
    description: 'Ready-to-install WordPress plugin with admin settings',
    icon: '📝',
    fileExtension: 'zip',
    available: true,
  },
];

export default function IntegrationDownloadPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('campaigns');
  const { user } = useAuth();
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Fetch campaign details
  const { data: campaign, isLoading, isError, error } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => campaignService.getCampaign(Number(id)),
    enabled: !!id,
  });

  // Fetch integration config
  const { data: integrationConfig } = useQuery({
    queryKey: ['integration-config'],
    queryFn: () => configService.getIntegrationConfig(),
  });

  // Fetch Integration FAQs
  const { data: faqData, isLoading: faqsLoading } = useQuery({
    queryKey: ['faqs', 'integration'],
    queryFn: () => faqService.getPublicFaqs('integration'),
  });

  const faqs: FaqQuestion[] = faqData?.categories[0]?.questions || [];

  const handleDownload = async (integrationType: IntegrationType) => {
    if (!campaign || !integrationConfig) return;

    const integration = integrations.find((i) => i.id === integrationType);
    if (!integration?.available) {
      toast.error(t('integration.coming_soon'));
      return;
    }

    if (integrationType === 'wordpress') {
      try {
        const response = await api.get(`/api/campaigns/${campaign.id}/download/wordpress`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `axetra-wordpress-${campaign.id}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(t('integration.download_success'));
      } catch {
        toast.error(t('integration.not_available'));
      }
      return;
    }

    // Generate the integration file content
    let fileContent = '';
    let fileName = '';

    switch (integrationType) {
      case 'php':
        fileName = `axetra-integration-${campaign.id}.php`;
        fileContent = phpTemplate
          .replaceAll('{{AXETRA_API_ENDPOINT}}', integrationConfig.traffic_filter_endpoint)
          .replaceAll('{{CAMPAIGN_UUID}}', campaign.uuid)
          .replaceAll('{{USER_ID}}', user?.id.toString() ?? '')
          .replaceAll('{{API_KEY}}', campaign.api_key);
        break;
      case 'javascript':
        fileName = `axetra-integration-${campaign.id}.js`;
        fileContent = generateJsIntegration(campaign.api_key);
        break;
      default:
        toast.error(t('integration.not_available'));
        return;
    }

    // Create download
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(t('integration.download_success'));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="p-6">
        <Alert variant="error">
          {error instanceof Error ? error.message : t('integration.error_loading')}
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('integration.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('integration.subtitle', { name: campaign.name })}
          </p>
        </div>
        <button
          onClick={() => navigate(`/campaigns/${campaign.id}`)}
          className="btn btn-secondary"
        >
          {t('common:actions.back')}
        </button>
      </div>

      {/* Campaign Credentials */}
      {integrationConfig && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Campaign Credentials</h2>
            <p className="text-sm text-gray-500 mb-4">
              Use these values to configure the plugin manually or connect your own integration.
            </p>
            <div className="space-y-3">
              {[
                { label: 'API Endpoint', value: integrationConfig.traffic_filter_endpoint },
                { label: 'Campaign UUID', value: campaign.uuid },
                { label: 'User ID', value: user?.id?.toString() ?? '' },
                { label: 'API Key', value: campaign.api_key, secret: true },
              ].map(({ label, value, secret }) => (
                <CredentialRow key={label} label={label} value={value} secret={!!secret} />
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Integration Options */}
      <div>
        <h2 className="text-lg font-semibold mb-4">{t('integration.select_integration')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {integrations.map((integration) => (
            <Card
              key={integration.id}
              className={`cursor-pointer transition-all ${
                selectedIntegration === integration.id
                  ? 'ring-2 ring-indigo-600'
                  : 'hover:shadow-md'
              } ${!integration.available ? 'opacity-60' : ''}`}
              onClick={() => integration.available && setSelectedIntegration(integration.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{integration.icon}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{integration.description}</p>
                    </div>
                  </div>
                  {!integration.available && (
                    <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                      {t('integration.coming_soon')}
                    </span>
                  )}
                </div>
                {integration.available && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(integration.id);
                    }}
                    className="mt-4 w-full btn btn-primary"
                  >
                    <svg
                      className="w-5 h-5 mr-2 inline-block"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('integration.download_button')}
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">{t('integration.faq_title', 'Frequently Asked Questions')}</h2>
          <div className="space-y-3">
            {faqsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="bg-gray-100 rounded-lg p-4 animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : faqs.length === 0 ? (
              <p className="text-gray-500 text-sm">No FAQs available.</p>
            ) : (
              faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-4 py-3 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900">{faqService.getTranslatedText(faq.question, faq.question_translations, i18n.language)}</span>
                    <svg
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-4 pb-3 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3 prose prose-sm max-w-none">
                      <ReactMarkdown>{faqService.getTranslatedText(faq.answer, faq.answer_translations, i18n.language)}</ReactMarkdown>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

// JavaScript SDK is not yet available — placeholder for future implementation
function generateJsIntegration(_apiKey: string): string {
  return '// JavaScript SDK coming soon';
}

function CredentialRow({ label, value, secret }: { label: string; value: string; secret: boolean }) {
  const [revealed, setRevealed] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const display = secret && !revealed ? '•'.repeat(Math.min(value.length, 32)) : value;

  return (
    <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
      <span className="w-32 flex-shrink-0 text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <code className="flex-1 text-sm text-gray-900 font-mono truncate">{display}</code>
      <div className="flex items-center gap-1 flex-shrink-0">
        {secret && (
          <button
            onClick={() => setRevealed((r) => !r)}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
            title={revealed ? 'Hide' : 'Reveal'}
          >
            {revealed ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        )}
        <button
          onClick={copy}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
          title="Copy"
        >
          {copied ? (
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
